import { hasHealthyAshbyMcpBase, type Connection } from '@renderer/hooks/use-connections'
import type { Provider } from '@renderer/hooks/use-providers'
import type {
  ConnectionPrerequisite,
  NestedActionView,
  QuestionsPrerequisite,
  StagedAction,
  StagedActionPrerequisite,
} from './types'

export const PREREQUISITE_UNSATISFIED_NESTED_VIEW_TYPE = 'prerequisiteUnsatisfied'
export const REQUEST_RECONNECT_STAGED_TOOLSET = 'request_reconnect'
export const REQUEST_RECONNECT_STAGED_TOOLNAME = 'reconnect'

// google-calendar / google-gmail / google-docs all authenticate through the
// single "google" storage connection, so match prerequisites at that level.
// `ashby-api-key` is the org-key upgrade of an Ashby connection, stored as an
// `ashby` row (mirrors `getStorageProviderName` on the backend).
function storageProvider(provider: string): string {
  if (provider.startsWith('google-')) return 'google'
  if (provider === 'ashby-api-key') return 'ashby'
  return provider
}

/**
 * Finds the stored connection that satisfies a prerequisite after normalizing
 * provider aliases. Ashby's API-key upgrade additionally requires a healthy
 * MCP OAuth base connection.
 */
function matchingConnection(
  prerequisite: ConnectionPrerequisite,
  connections: Array<Connection> | undefined,
): Connection | undefined {
  const connection = connections?.find(
    (connection) =>
      (!prerequisite.connectionId || connection.id === prerequisite.connectionId) &&
      storageProvider(connection.providerName) === storageProvider(prerequisite.provider),
  )
  // An API key only expands an existing Ashby MCP connection; it cannot
  // independently satisfy the prerequisite without current OAuth scopes.
  if (prerequisite.provider === 'ashby-api-key' && !hasHealthyAshbyMcpBase(connections)) {
    return undefined
  }
  return connection
}

export function getUnsatisfiedConnectionPrerequisite(
  action: Pick<StagedAction, 'prerequisites'>,
  connections: Array<Connection> | undefined,
): ConnectionPrerequisite | null {
  return (
    action.prerequisites?.find((prerequisite): prerequisite is ConnectionPrerequisite => {
      if (prerequisite.type !== 'connection') return false
      const connection = matchingConnection(prerequisite, connections)
      return !connection || connection.hasExpired
    }) ?? null
  )
}

/** The questions a planner v2 action still asks before it's addressed and drafted, if any. */
export function getPendingQuestions(
  action: { prerequisites?: Array<StagedActionPrerequisite>; lastInvokedAt?: string | null } | undefined,
): QuestionsPrerequisite | null {
  if (!action || action.lastInvokedAt) return null
  return (
    action.prerequisites?.find(
      (prerequisite): prerequisite is QuestionsPrerequisite =>
        prerequisite.type === 'questions' && prerequisite.questions.length > 0,
    ) ?? null
  )
}

function prerequisiteNestedView(
  view: { nestedViews?: Array<NestedActionView> } | undefined,
): NestedActionView | undefined {
  return view?.nestedViews?.find((candidate) => candidate.type === PREREQUISITE_UNSATISFIED_NESTED_VIEW_TYPE)
}

/**
 * CTA label for an unsatisfied connection prerequisite. An action with a single
 * prerequisite (request_reconnect) has the backend compose the exact label
 * ("Connect Linear" / "Reconnect Linear") as the nested view's title, so reuse
 * it. An action that can be blocked on several providers in turn (an auto-mode
 * plan) has no such title — one nested view can't track which prerequisite is
 * currently unsatisfied — so name the provider from the connections catalog,
 * the same source the integrations list labels its cards from. Title-casing the
 * provider id is the last resort: it only reads right for single-word providers
 * ("linear", not "google-gmail").
 */
export function getConnectionPrerequisiteCtaLabel(
  action: { view?: { nestedViews?: Array<NestedActionView> } },
  prerequisite: ConnectionPrerequisite,
  connections: Array<Connection> | undefined,
  providers?: Array<Provider>,
): string {
  const nestedTitle = prerequisiteNestedView(action.view)?.view.title
  if (nestedTitle) return nestedTitle
  const displayName =
    providers?.find((provider) => provider.id === prerequisite.provider)?.name ??
    prerequisite.provider
      .split('-')
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ')
  const known = matchingConnection(prerequisite, connections) || prerequisite.connectionId
  return known ? `Reconnect ${displayName}` : `Connect ${displayName}`
}

/**
 * Footer warning for an action blocked on a connection, taking the place of the
 * view's own `invokeWarning` while the block lasts: what approving would hand
 * over is moot until the action can run at all.
 *
 * Only for cards whose body doesn't already say it. A `request_reconnect` card
 * swaps in a `prerequisiteUnsatisfied` nested view titled with this same CTA, so
 * warning there would repeat the card back to itself; an auto-mode plan keeps
 * its plan body (see `surfaceAutoModeRequest`), which leaves the footer the only
 * place the block is named.
 */
export function getConnectionPrerequisiteWarning(
  action: { view?: { nestedViews?: Array<NestedActionView> } },
  prerequisite: ConnectionPrerequisite,
  connections: Array<Connection> | undefined,
  providers?: Array<Provider>,
): string | null {
  if (prerequisiteNestedView(action.view)) return null
  return `${getConnectionPrerequisiteCtaLabel(action, prerequisite, connections, providers)} to continue`
}

export function getPrerequisiteDisplayView<V extends { nestedViews?: Array<NestedActionView> }>(action: {
  view?: V
}): V | undefined {
  const view = action.view
  const nested = prerequisiteNestedView(view)
  if (!view || !nested) {
    return view
  }

  return { ...view, ...nested.view, nestedViews: [] } as V
}

export function isRequestReconnectAction(action: Pick<StagedAction, 'toolSet' | 'toolName'>): boolean {
  return action.toolSet === REQUEST_RECONNECT_STAGED_TOOLSET && action.toolName === REQUEST_RECONNECT_STAGED_TOOLNAME
}
